import React, { useEffect, useRef, useState } from 'react'; // Import useEffect, useRef, and useState
import { motion } from 'framer-motion';
import {
    Activity,
    TrendingUp,
    Heart,
    BrainCircuit,
    CheckCircle,
    Zap,
    Apple, // Keep Apple import in case it's used elsewhere, though it's removed from the specific card
    // Watch, // Removed Lucide Watch import
    Coins,
    Sparkles,
    KeyRound,
    WalletCards,
    ChartBarStacked,
    WandSparkles,
    Pill, // Pill is no longer used for the top icon, but kept if used elsewhere
    BedDouble,
} from 'lucide-react';

// A simple cn utility to merge class names
const cn = (...classes) => {
    return classes.filter(Boolean).join(' ');
}

// Accent Colors (These can remain consistent or be themed if needed)
const FORM_ACTIVE_RED = '#FF4B6C'; // Used for icon color consistency (Strain)
const FORM_SLEEP_BLUE = '#2EA8FF'; // Used for icon color consistency (Sleep)
const FORM_RECOVERY_GREEN = '#32E583'; // Used for icon color consistency (Recovery)

// Define slightly lighter and darker shades for primary text color for gradient
const DARK_MODE_PRIMARY_LIGHT = 'rgb(200, 190, 192)';
const DARK_MODE_PRIMARY_DARK = 'rgb(170, 155, 158)';
const LIGHT_MODE_PRIMARY_LIGHT = '#A09590';
const LIGHT_MODE_PRIMARY_DARK = '#706560';


// --- Global Font Injector and Theme Variables ---
const GlobalFontAndThemeInjector = () => (
    <style jsx global>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap'); /* Added IBM Plex Mono and heavier Inter weights */
    body {
        font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
        transition: background-color 0.3s ease; /* Smooth transition for background */
        overflow-x: hidden; /* Prevent horizontal scrolling */
    }
    /* Ensure headings and other text elements also inherit or use Inter */
    h1, h2, h3, h4, h5, h6, span, div, button, input, textarea, select {
        font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
        /* Subtle text shadow */
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
    }
    /* Apply IBM Plex Mono specifically to paragraph tags */
    p {
        font-family: 'IBM Plex Mono', monospace;
        /* Subtle text shadow */
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
        line-height: 1.6; /* Ensure sufficient line height to prevent clipping */
    }
      /* Override IBM Plex Mono for specific paragraphs that should use Inter */
      p.font-inter {
        font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
      }

    /* Class to remove text shadow */
    .no-text-shadow {
        text-shadow: none;
    }


    /* Custom Shadow for more depth */
    .vision-shadow {
        box-shadow:
            0 15px 35px rgba(0, 0, 0, 0.4), /* Larger, softer shadow */
            0 5px 15px rgba(0, 0, 0, 0.2); /* Smaller, sharper shadow */
    }

    /* More subtle shadow for specific elements */
    .vision-shadow-subtle {
        box-shadow:
            0 5px 15px rgba(0, 0, 0, 0.2), /* Smaller, softer shadow */
            0 2px 5px rgba(0, 0, 0, 0.1); /* Even smaller, sharper shadow */
    }

    /* Custom Inner Shadow for neumorphic feel */
    .vision-inner-shadow {
        box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.3), inset 0 -2px 5px rgba(255, 255, 255, 0.05);
    }

    /* Hero image card container for the glow effect */
    .hero-image-card-container {
        position: relative; /* Needed for absolute positioning of pseudo-element */
        overflow: hidden; /* Clips the pseudo-element to the rounded corners */
    }

    /* Pseudo-element for the glow effect */
    .hero-image-card-container::before {
        content: '';
        position: absolute;
        top: -10px; /* Adjust to control glow spread */
        left: -10px; /* Adjust to control glow spread */
        right: -10px; /* Adjust to control glow spread */
        bottom: -10px; /* Adjust to control glow spread */
        background: conic-gradient(from 0deg at 50% 50%,
            ${FORM_ACTIVE_RED} 0%,
            ${FORM_SLEEP_BLUE} 33%,
            ${FORM_RECOVERY_GREEN} 66%,
            ${FORM_ACTIVE_RED} 100%
        ); /* Conic gradient for the glow */
        z-index: -1; /* Position behind the content */
        filter: blur(15px); /* Adjust to control blur amount */
        border-radius: 32px; /* Match the parent border radius */
    }


    /* CSS Variables for Theming */
    :root {
        /* Dark Mode Defaults */
        --page-bg-start: hsl(240, 10%, 8%);
        --page-bg-end: hsl(240, 10%, 5%);
        --glass-surface-bg: hsla(230, 10%, 18%, 0.75);
        --glass-surface-border: hsla(230, 10%, 40%, 0.6);
        --text-primary: rgb(188, 171, 173); /* Updated dark mode text primary color */
        --text-secondary: hsl(0, 0%, 85%);
        --glass-inner-bg: hsla(230, 10%, 20%, 0.5);
        --glass-inner-border: hsla(230, 10%, 40%, 0.6);

        --glass-pill-bg: hsla(230, 10%, 25%, 0.6);
        --glass-pill-border: hsla(230, 10%, 45%, 0.7);

          /* More transparent glass for the nav bar - Reduced alpha */
        --glass-nav-bg: hsla(230, 10%, 18%, 0.3); /* Reduced alpha from 0.5 to 0.3 */
        --glass-nav-border: hsla(230, 10%, 40%, 0.3); /* Reduced alpha from 0.5 to 0.3 */
    }
    @media (prefers-color-scheme: light) {
        :root {
            /* Light Mode Colors based on RGB(216, 205, 213) */
            --page-bg-start: hsl(327, 12%, 85%); /* Adjusted lightness */
            --page-bg-end: hsl(327, 12%, 92%); /* Adjusted lightness */
            --glass-surface-bg: hsla(327, 12%, 95%, 0.75); /* Lighter glass */
            --glass-surface-border: hsla(327, 5%, 70%, 0.6); /* Lighter border */
            --text-primary: #887A72; /* Updated light mode text primary color */
            --text-secondary: hsl(0, 0%, 30%); /* Darker secondary text */
            --glass-inner-bg: hsla(327, 10%, 90%, 0.5); /* Lighter inner glass */
            --glass-inner-border: hsla(327, 5%, 65%, 0.6); /* Lighter inner border */
            --glass-pill-bg: hsla(327, 10%, 88%, 0.6); /* Lighter pill glass */
            --glass-pill-border: hsla(327, 5%, 75%, 0.7); /* Lighter pill border */

            /* Light mode shadows (subtler) */
            .vision-shadow {
                box-shadow:
                    0 8px 20px rgba(0, 0, 0, 0.1),
                    0 3px 8px rgba(0, 0, 0, 0.08);
            }
              /* More subtle shadow for specific elements in light mode */
            .vision-shadow-subtle {
                box-shadow:
                    0 3px 8px rgba(0, 0, 0, 0.08),
                    0 1px 3px rgba(0, 0, 0, 0.05);
            }
            .vision-inner-shadow {
                box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1), inset 0 -1px 3px rgba(255, 255, 255, 0.1);
            }
              /* Subtle text shadow for light mode */
            h1, h2, h3, h4, h5, h6, p, span, div, button, input, textarea, select {
                text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
            }

              /* More transparent glass for the nav bar in light mode - Reduced alpha */
            --glass-nav-bg: hsla(327, 12%, 95%, 0.3); /* Reduced alpha from 0.5 to 0.3 */
            --glass-nav-border: hsla(327, 5%, 70%, 0.3); /* Reduced alpha from 0.3 to 0.3 */
        }
    }
    /* Apply variables */
    .themed-background {
        background: linear-gradient(180deg, var(--page-bg-start) 0%, var(--page-bg-end) 100%);
    }
    .themed-glass-surface {
        background-color: var(--glass-surface-bg);
        border-color: var(--glass-surface-border);
    }

    .themed-glass-inner {
        background-color: var(--glass-inner-bg);
        border-color: var(--glass-inner-border);
    }

    .themed-glass-pill {
        background-color: var(--glass-pill-bg);
        border-color: var(--glass-pill-border);
    }
    .themed-text-primary {
        color: var(--text-primary);
    }
    .themed-text-secondary {
        color: var(--text-secondary);
    }

    /* Specific styling for the nav bar glass */
    .themed-glass-nav {
          background-color: var(--glass-nav-bg);
          border-color: var(--glass-nav-border);
    }

    /* Base class for gradient text */
    .gradient-text {
        background-clip: text;
        -webkit-background-clip: text;
        color: transparent;
    }

    /* Conical gradient for the 'Form' heading and 'Your Daily FormScore' heading */
    .conical-gradient-text {
          background-image: conic-gradient(from 90deg at 50% 50%,
              ${FORM_ACTIVE_RED} 0%,
              ${FORM_SLEEP_BLUE} 33%,
              ${FORM_RECOVERY_GREEN} 66%,
              ${FORM_ACTIVE_RED} 100%
          );
    }

    /* Linear gradient for Hero Heading (subtle shades of primary text) */
    .hero-heading-gradient {
        background-image: linear-gradient(to right,
            ${DARK_MODE_PRIMARY_LIGHT},
            var(--text-primary),
            ${DARK_MODE_PRIMARY_DARK}
        );
    }
     @media (prefers-color-scheme: light) {
        .hero-heading-gradient {
            background-image: linear-gradient(to right,
                ${LIGHT_MODE_PRIMARY_LIGHT},
                var(--text-primary),
                ${LIGHT_MODE_PRIMARY_DARK}
            );
        }
    }

    /* Linear gradient for Why Form Heading (subtle shades of primary text) */
    .why-form-heading-gradient {
        background-image: linear-gradient(to right,
            ${DARK_MODE_PRIMARY_LIGHT},
            var(--text-primary),
            ${DARK_MODE_PRIMARY_DARK}
        );
    }
     @media (prefers-color-scheme: light) {
        .why-form-heading-gradient {
            background-image: linear-gradient(to right,
                ${LIGHT_MODE_PRIMARY_LIGHT},
                var(--text-primary),
                ${LIGHT_MODE_PRIMARY_DARK}
            );
        }
    }


    /* Subtle shadow for icons and images */
    svg, img {
        filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.2));
    }
    @media (prefers-color-scheme: light) {
        svg, img {
            filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.1));
        }
    }
    `}</style>
);

// Placeholder images (using different ones for variety and closer aspect ratios)
const DashboardImage1 = `https://placehold.co/400x533/1A1D23/E0E0E0?text=Dashboard+1&font=Inter`; // 3:4
const DashboardImage2 = `https://placehold.co/533x400/1A1D23/E0E0E0?text=Dashboard+2&font=Inter`; // 4:3
const DashboardImage3 = `https://placehold.co/400x533/1A1D23/E0E0E0?text=Dashboard+3&font=Inter`; // 3:4

// Corrected capitalization for Recovery images
const RecoveryImage1 = `/AccessoryGauge.jpg`;
const RecoveryImage2 = `/FormScoreTrend.jpg`;
const RecoveryImage3 = `/FormScoreGauge.jpg`;

const SleepImage1 = `https://placehold.co/400x533/1A1D23/2EA8FF?text=Sleep+1&font=Inter&txtcolor=1A1D23`; // 3:4
const SleepImage2 = `https://placehold.co/533x400/1A1D23/2EA8FF?text=Sleep+2&font=Inter&txtcolor=1A1D23`; // 4:3

const ActivityImage1 = `https://placehold.co/400x533/1A1D23/FF4B6C?text=Activity+1&font=Inter&txtcolor=1A1D23`; // 3:4
const ActivityImage2 = `https://placehold.co/533x400/1A1D23/FF4B6C?text=Activity+2&font=Inter&txtcolor=1A1D23`; // 4:3

const BodyImage1 = `https://placehold.co/400x533/1A1D23/E0E0E0?text=Body+1&font=Inter`; // 3:4
const BodyImage2 = `https://placehold.co/533x400/1A1D23/E0E0E0?text=Body+2&font=Inter`; // 4:3
const BodyImage3 = `https://placehold.co/400x533/1A1D23/E0E0E0?text=Body+3&font=Inter`; // 3:4



// Animation variants
const fadeIn = {
    hidden: { opacity: 0, scale: 0.95 }, // Slightly smaller initial scale
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] } }, // Increased duration slightly
};

const slideInLeft = {
    hidden: { opacity: 0, x: -70 }, // Increased slide distance
    visible: { opacity: 1, x: 0, transition: { duration: 0.9, ease: [0.6, -0.05, 0.01, 0.99] } }, // Increased duration
};

const slideInRight = {
    hidden: { opacity: 0, x: 70 }, // Increased slide distance
    visible: { opacity: 1, x: 0, transition: { duration: 0.9, ease: [0.6, -0.05, 0.01, 0.99] } }, // Increased duration
};

// FeatureCard Component - Updated for nested glassy elements, increased radius, enhanced shadow, and theming
const FeatureCard = ({ title, description, icon, delay = 0 }) => (
    <motion.div
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        transition={{ delay }}
        className="backdrop-filter backdrop-blur-xl rounded-[32px] p-6 border h-full flex flex-col vision-shadow themed-glass-surface"
    >
        {/* Pill for Title and Icon - Themed */}
        <div
            className="inline-flex items-center gap-3 mb-4 px-4 py-2 rounded-full backdrop-filter backdrop-blur-lg border self-start vision-shadow themed-glass-pill"
        >
            {icon}
            <h3 className="text-lg font-semibold themed-text-primary">{title}</h3>
        </div>
        {/* Card for Description - Themed */}
        <div
            className="flex-grow backdrop-filter backdrop-blur-lg rounded-[24px] p-4 border vision-inner-shadow themed-glass-inner"
        >
            {/* Applied IBM Plex Mono font */}
            <p className="text-sm leading-relaxed themed-text-secondary font-mono">{description}</p>
        </div>
    </motion.div>
);

// Pricing Card Component
const PricingCard = ({ delay = 0 }) => {
    const [selectedOption, setSelectedOption] = useState('yearly'); // State to manage the selected option

    const pricing = {
        monthly: { price: '$1.99', period: '/mo' },
        yearly: { price: '$0.99', period: '/mo ($11.88/yr)' },
        lifetime: { price: '$39', period: 'for lifetime access' },
    };

    return (
        <motion.div
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay }}
            className="backdrop-filter backdrop-blur-xl rounded-[32px] p-6 border h-full flex flex-col vision-shadow themed-glass-surface"
        >
            {/* Pill for Title and Icon - Themed */}
            <div
                className="inline-flex items-center gap-3 mb-4 px-4 py-2 rounded-full backdrop-filter backdrop-blur-lg border self-start vision-shadow themed-glass-pill"
            >
                <WalletCards className="w-6 h-6" style={{ color: 'var(--text-primary)' }} />
                <h3 className="text-lg font-semibold themed-text-primary">Fair Pricing</h3>
            </div>

            {/* Inner Card for Pricing Information */}
            <div className="flex-grow backdrop-filter backdrop-blur-lg rounded-[24px] p-4 border vision-inner-shadow themed-glass-inner flex flex-col items-center justify-center gap-6"> {/* Added flex-grow, flex-col, items-center, justify-center, and gap-6 */}
                {/* Initial Free Period Text */}
                <p className="text-sm leading-relaxed text-center themed-text-secondary font-mono">
                    Form will initially be free to users for 6-12 months after release. Eventually, there will be 3 available pricing options:
                </p>

                {/* Pricing Selector */}
                <div className="inline-flex rounded-full backdrop-filter backdrop-blur-lg border vision-shadow-subtle themed-glass-pill p-1">
                    {Object.keys(pricing).map((option) => (
                        <button
                            key={option}
                            className={cn(
                                "px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200",
                                selectedOption === option
                                    ? "themed-glass-surface themed-text-primary vision-shadow" // Active state styling
                                    : "themed-text-secondary hover:themed-glass-inner" // Inactive state styling
                            )}
                            onClick={() => setSelectedOption(option)}
                        >
                            {option.charAt(0).toUpperCase() + option.slice(1)} {/* Capitalize first letter */}
                        </button>
                    ))}
                </div>

                {/* Price Display */}
                <div className="text-center"> {/* Wrapped price display in a div for centering */}
                    <p className="text-2xl font-bold themed-text-primary">{pricing[selectedOption].price}</p>
                    <p className="text-sm themed-text-secondary">{pricing[selectedOption].period}</p>
                </div>
            </div>
        </motion.div>
    );
};


// TabSection Component - Updated to integrate images within the card
const TabSection = ({ title, description, sectionColor, images, isLeft = true, isConicalGradientTitle = false }) => (
    <motion.section
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="py-20 md:py-28"
    >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 flex justify-center"> {/* Centering the single card */}
            {/* Single Glassy Card for Text and Images */}
            <motion.div
                variants={fadeIn} // Using fadeIn for the whole card
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="backdrop-filter backdrop-blur-xl rounded-[32px] p-8 border vision-shadow themed-glass-surface w-full max-w-4xl flex flex-col md:flex-row items-center gap-8 md:gap-12" // Added flex layout for inner content
            >
                {/* Text Content Area */}
                <div className={cn("flex flex-col justify-center w-full md:w-1/2", isLeft ? "md:order-1" : "md:order-2")}> {/* Adjusted width and order */}
                    {/* Pill for Title - Themed */}
                    <div
                        className="inline-flex items-center gap-1 mb-4 px-2 py-1 rounded-full backdrop-filter backdrop-blur-lg border self-center md:self-start vision-shadow themed-glass-pill" // Adjusted padding (px-4 py-2 to px-2 py-1) and gap (gap-3 to gap-1)
                    >
                        {/* Added Form Icon before the title */}
                        <img
                            src="/Form_Icon_Color-removebg.png"
                            alt="Form app icon" // Improved alt text
                            className="w-10 h-10" // Adjusted size
                        />
                        <h2 className={cn("text-xl font-semibold", isConicalGradientTitle ? "gradient-text conical-gradient-text" : "themed-text-primary")} style={{ color: !isConicalGradientTitle && sectionColor ? sectionColor : undefined }}>{title}</h2> {/* Conditionally apply gradient classes or themed color */}
                    </div>
                    {/* Card for Description - Themed */}
                    <div
                        className="backdrop-filter backdrop-blur-lg rounded-[24px] p-4 border vision-inner-shadow themed-glass-inner"
                    >
                        <p className="text-sm leading-relaxed themed-text-secondary font-mono">{description}</p>
                    </div>
                </div>
                {/* Image Content Area */}
                <div className={cn("w-full md:w-1/2 flex justify-center", isLeft ? "md:order-2" : "md:order-1")}> {/* Adjusted width and order */}
                    <div className={cn(
                        "grid gap-4 w-full max-w-sm", // Added w-full and max-w-sm for responsiveness
                        images && images.length === 2 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3" // Adjust grid based on number of images
                    )}>
                        {images && images.map((imgSrc, index) => (
                            <img
                                key={index}
                                src={imgSrc}
                                alt={`${title} Screenshot ${index + 1}`}
                                className={cn(
                                    // Increased corner radius to rounded-2xl
                                    "rounded-2xl shadow-lg w-full h-auto object-cover",
                                    images.length === 3 && index === 0 ? "col-span-2 md:col-span-1" : "" // Span first image for 3-image layout on mobile
                                )}
                            />
                        ))}
                    </div>
                </div>
            </motion.div>
        </div>
    </motion.section>
);

const FormAppLandingPage = () => {
    const heroImageRef = useRef(null); // Create a ref for the hero image

    // Effect to force GIF replay on component mount using a timestamp query parameter
    useEffect(() => {
        if (heroImageRef.current) {
            const originalSrc = '/HeroPicture.gif'; // Define the original source
            // Append a timestamp as a query parameter to bypass caching
            heroImageRef.current.src = `${originalSrc}?t=${Date.now()}`;
        }
    }, []); // Empty dependency array ensures this runs only on mount

    return (
        <>
            <GlobalFontAndThemeInjector />
            <div className="min-h-screen text-white selection:bg-purple-500 selection:text-white themed-background overflow-x-hidden"> {/* Added overflow-x-hidden */}
                {/* Floating Menu Bar - Increased height and width, added image asset, and theming */}
                <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50 w-11/12 max-w-screen-xl"> {/* Adjusted width and centering */}
                    <div
                        className="flex items-center justify-between px-6 py-2 rounded-full backdrop-filter backdrop-blur-xl border vision-shadow themed-glass-nav w-full" // Changed py-3 to py-2 and added justify-between
                    >
                        {/* Left side: Form Icon and Title */}
                        <div className="flex items-center">
                            {/* Form Icon */}
                            <img
                                src="/Form_Icon_Color-removebg.png"
                                alt="Form app icon" // Improved alt text
                                className="w-10 h-10 mr-0.5" // Reduced margin-right
                            />
                            {/* Moved Form heading to nav bar and styled with new gradient class */}
                            <h1
                                className="text-xl font-bold gradient-text conical-gradient-text" // Applied base and conical gradient classes
                            >
                                Form
                            </h1>
                        </div>

                        {/* Right side: Join Waitlist Button */}
                        <a
                            href="mailto:info@jdymedia.co?subject=I%20want%20to%20join%20the%20waitlist"
                            className="flex items-center gap-2 backdrop-filter backdrop-blur-lg rounded-full px-4 py-2 border text-sm vision-shadow themed-glass-pill themed-text-primary hover:themed-glass-surface transition-colors duration-200" // Styled as a glassy pill button
                        >
                            Join Waitlist
                        </a>

                        {/* You can add navigation links or other elements here */}
                        {/* <nav>
                            <ul className="flex space-x-4">
                                <li><a href="#features" className="themed-text-primary hover:text-purple-400 transition-colors duration-200">Features</a></li>
                                <li><a href="#pricing" className="themed-text-primary hover:text-purple-400 transition-colors duration-200">Pricing</a></li>
                            </ul>
                        </nav> */}
                    </div>
                </div>
                {/* Added padding to the main content to prevent it from being hidden by the fixed header */}
                <div className="pt-20"> {/* Changed pt-24 to pt-20 */}
                    {/* Hero Section */}
                    <header className="py-20 md:py-32">
                        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
                            {/* Applied glassy styling to the main content container - Increased radius and enhanced shadow and theming */}
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }}
                                className="backdrop-filter backdrop-blur-xl rounded-[32px] p-8 border max-w-4xl mx-auto vision-shadow themed-glass-surface flex flex-col items-center w-full" // Added w-full
                            >
                                {/* Main Heading - Moved inside the card and adjusted bottom margin */}
                                <p
                                    className="text-4xl sm:text-5xl md:text-6xl font-bold mb-12 font-inter themed-text-primary no-text-shadow gradient-text hero-heading-gradient" // Adjusted font size classes, changed mb-8 to mb-12, and changed font-extrabold to font-bold, added no-text-shadow and gradient classes
                                >
                                    <span style={{ whiteSpace: 'nowrap' }}>Your Personalized</span><br />Health & Recovery Platform
                                </p>

                                {/* Subheading */}
                                <p
                                    className="text-xl md:text-2xl text-base sm:text-lg mb-8 max-w-3xl mx-auto leading-relaxed themed-text-secondary" // Added mobile font sizes
                                >
                                    Powered by a device you already own – your Apple Watch.
                                </p>

                                {/* Added the Hero Picture here - Wrapped in a glass card with increased border and padding and the new glow class */}
                                <div
                                    className="backdrop-filter backdrop-blur-lg rounded-[32px] p-1 border mb-8 vision-shadow themed-glass-surface mx-auto max-w-sm w-full hero-image-card-container" // Changed padding from p-1 to p-0.5, added max-w-sm and w-full for responsiveness, added hero-image-card-container class
                                >
                                    {/* Pseudo-element for glow is handled by the CSS class on the container */}
                                    <img
                                        ref={heroImageRef} // Assign the ref to the image
                                        src="/HeroPicture.gif" // Initial src, will be updated by useEffect
                                        alt="Illustration showing health data insights" // Improved alt text
                                        className="rounded-[32px] shadow-lg w-full h-auto" // Made image responsive within its container, changed rounded-[24px] to rounded-[32px]
                                    />
                                </div>
                                {/* Moved Works with Apple Health Pill here */}
                                <motion.div
                                    variants={fadeIn}
                                    initial="hidden"
                                    animate="visible"
                                    transition={{ delay: 0.5 }}
                                    className="inline-flex items-center justify-center mt-6" // Removed mb-8
                                >
                                    <div
                                        className="flex items-center gap-2 backdrop-filter backdrop-blur-lg rounded-full px-6 py-3 border text-sm vision-shadow themed-glass-pill"
                                    >
                                        <img
                                            src="/Apple_Health_badge_US-UK_blk_sRGB.svg"
                                            alt="Apple Health badge"
                                            className="w-32 h-12" // Doubled size from w-16 h-6
                                        />
                                    </div>
                                </motion.div>
                            </motion.div>
                        </div>
                    </header>
                    {/* Core Principles Section */}
                    <section className="py-20 md:py-28">
                        <div className="container mx-auto px-4 md:px-6 lg:px-8">
                            <h2 className="text-4xl md:text-5xl font-bold mb-24 text-center themed-text-primary flex items-center justify-center gap-4 no-text-shadow gradient-text why-form-heading-gradient"> {/* Increased mb-16 to mb-24 and changed font-light to font-bold, added flex, items-center, justify-center, gap-4, added no-text-shadow and gradient classes */}
                                Why Form
                                {/* Added Form Icon next to the heading */}
                                <img
                                    src="/Form_Icon_Color-removebg.png"
                                    alt="Form app icon" // Improved alt text
                                    className="w-20 h-20" // Adjusted size to double (w-10 h-10 to w-20 h-20)
                                />
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                                <FeatureCard
                                    title="Privacy First"
                                    description="Your health data stays where it belongs: on your Apple Watch and iPhone. All calculations are performed directly on your device. We don't store any of your information."
                                    icon={<CheckCircle className="w-6 h-6 text-green-400" />}
                                    delay={0} // Adjusted delay
                                />
                                <FeatureCard
                                    title="Effortless Setup"
                                    description="No accounts, no hassle. Just grant HealthKit permissions and you're ready to go."
                                    icon={<Zap className="w-6 h-6 text-yellow-400" />}
                                    delay={0.15} // Adjusted delay
                                />
                                {/* Moved Holistic Tracking here */}
                                <FeatureCard
                                    title="Holistic Tracking"
                                    description="Monitor your FormScore, Recovery, Sleep, Strain, and Body metrics – all in one place, powered by your Apple Watch."
                                    icon={<img src="/WatchIcon.png" alt="Watch Icon" className="w-6 h-6" />} // Replaced Lucide Watch with image
                                    delay={0.3} // Adjusted delay
                                />
                                <FeatureCard
                                    title="Native & Optimized"
                                    description="Built entirely in Swift and SwiftUI for iPhone, leveraging the latest Apple SDKs for a seamless, highly-native experience."
                                    // Replaced Lucide Apple icon with image from public directory
                                    icon={<img src="/SwiftUILogo.png" alt="SwiftUI Logo" className="w-6 h-6" />}
                                    delay={0.45} // Adjusted delay
                                />
                                <FeatureCard
                                    title="Intelligent Insights"
                                    description="Introducing Spotter, an unobtrusive AI assistant that provides insights when you need them, while staying out of your way the rest of the time."
                                    icon={<WandSparkles className="w-6 h-6 text-blue-400" />}
                                    delay={0.6} // Adjusted delay
                                />
                                {/* Replaced Fair Pricing FeatureCard with the new PricingCard component */}
                                <PricingCard delay={0.75} />
                            </div>
                        </div>
                    </section>
                    {/* Tab Sections */}
                    <TabSection
                        title="Your Daily FormScore"
                        description="The Dashboard gives you a holistic view of your readiness with the FormScore. It combines your recovery, sleep, and activity metrics for a quick understanding of your day."
                        images={["/AccessoryGauge.jpg", "/FormScoreTrend.jpg", "/FormScoreGauge.jpg"]} // 3 images
                        sectionColor={'var(--text-primary)'}
                        isLeft={true}
                        isConicalGradientTitle={true} // Set this to true for the conical gradient
                    />
                    <TabSection
                        title="Optimize Your Recovery"
                        description="Track your body's recovery with detailed metrics. Understand how well you're prepared for strain."
                        // Corrected capitalization for image paths
                        images={["/RecoveryScoreTrend.jpg", "/RecoveryScoreGauge.jpg", "/RecoveryVitalsViewer.jpg"]} // 3 images
                        isLeft={false}
                        sectionColor={FORM_RECOVERY_GREEN}
                    />
                    <TabSection
                        title="Master Your Sleep"
                        description="Analyze your sleep stages, duration, and quality. Identify patterns and improve your rest."
                        images={["/SleepGaugeView.jpg", "/HypnogramView.jpg"]} // 2 images
                        sectionColor={FORM_SLEEP_BLUE}
                        isLeft={true}
                    />
                    <TabSection
                        title="Track Your Activity"
                        description="Monitor your strain and activity levels. See how your workouts impact your body."
                        images={[ActivityImage1, ActivityImage2]} // 2 images
                        isLeft={false}
                        sectionColor={FORM_ACTIVE_RED}
                    />
                    <TabSection
                        title="Body Composition"
                        description="Track key body metrics like body fat percentage, BMI, weight and muscle mass. Gain a deeper understanding of your physical health."
                        images={[BodyImage1, BodyImage2, BodyImage3]} // 3 images
                        sectionColor={'var(--text-primary)'}
                        isLeft={true}
                    />
                    {/* The Form Difference Section */}
                    <section className="py-20 md:py-28">
                        <div className="container mx-auto px-4 md:px-6 lg:px-8">
                            <motion.div
                                variants={fadeIn}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.2 }}
                                className="backdrop-filter backdrop-blur-xl rounded-[32px] p-10 md:p-12 border max-w-4xl mx-auto text-center vision-shadow-subtle themed-glass-surface w-full" // Applied vision-shadow-subtle and w-full
                            >
                                <div
                                    className="inline-flex items-center justify-center gap-3 mb-6 px-6 py-2 rounded-full backdrop-filter backdrop-blur-lg border vision-shadow-subtle themed-glass-pill" // Applied vision-shadow-subtle and changed py-3 to py-2
                                >
                                    {/* Sparkles icon is light blue */}
                                    <ChartBarStacked className="w-8 h-8" style={{ color: 'lightskyblue' }} />
                                    <h2 className="text-xl font-bold themed-text-primary"> {/* Reduced font size */}
                                        The Form Difference
                                    </h2>
                                </div>
                                <div className="backdrop-filter backdrop-blur-lg rounded-[24px] p-4 border vision-inner-shadow themed-glass-inner">
                                    <p className="text-lg md:text-xl leading-relaxed themed-text-secondary">
                                        We believe that your health data belongs to you. We're committed to providing a premium experience
                                        without the premium price tag. No extortionate subscriptions, no hidden fees – just your data, on
                                        your device.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </section>
                    {/* Unlock Your Apple Watch Section */}
                    <section className="py-20 md:py-28">
                        <div className="container mx-auto px-4 md:px-6 lg:px-8">
                            <motion.div
                                variants={fadeIn}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.2 }}
                                className="backdrop-filter backdrop-blur-xl rounded-[32px] p-10 md:p-12 border max-w-4xl mx-auto text-center vision-shadow-subtle themed-glass-surface w-full flex flex-col gap-6" // Applied vision-shadow-subtle, w-full, and added flex-col and gap-6
                            >
                                <div
                                    className="inline-flex items-center justify-center gap-3 px-6 py-2 rounded-full backdrop-filter backdrop-blur-lg border vision-shadow-subtle themed-glass-pill" // Applied vision-shadow-subtle and changed py-3 to py-2, removed mb-6
                                >
                                    {/* Key icon is purple */}
                                    <KeyRound className="w-8 h-8 text-purple-400" />
                                    <h2 className="text-xl font-bold themed-text-primary"> {/* Reduced font size */}
                                        Unlock Your Apple Watch's Full Potential
                                    </h2>
                                </div>
                                {/* Restored the three paragraphs in nested glass cards */}
                                <div className="backdrop-filter backdrop-blur-lg rounded-[24px] p-4 border vision-inner-shadow themed-glass-inner">
                                    <p className="text-lg md:text-xl leading-relaxed themed-text-secondary">
                                        Your Apple Watch is a powerhouse of health data, yet navigating Apple Health can often feel like you opened up a giant spreadsheet.
                                    </p>
                                </div>
                                <div className="backdrop-filter backdrop-blur-lg rounded-[24px] p-4 border vision-inner-shadow themed-glass-inner">
                                    <p className="text-lg md:text-xl leading-relaxed themed-text-secondary">
                                        Form cuts through this noise, acting as your key to unlock a clearer picture. We elegantly synthesize the rich stream of information your Watch meticulously logs—transforming complex metrics into intuitive, actionable insights.
                                    </p>
                                </div>
                                <div className="backdrop-filter backdrop-blur-lg rounded-[24px] p-4 border vision-inner-shadow themed-glass-inner">
                                    <p className="text-lg md:text-xl leading-relaxed themed-text-secondary">
                                        With Form, you gain a streamlined, comprehensive view of your well-being, empowering you to make informed decisions about your health journey, effortlessly.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </section>
                    {/* Footer */}
                    <footer className="py-12 themed-glass-surface text-center text-sm themed-text-secondary border-t border-gray-700"> {/* Added themed-glass-surface and border */}
                        <div className="container mx-auto px-4">
                            <p>&copy; 2024 Form. All rights reserved.</p>
                        </div>
                    </footer>
                </div>
            </div>
        </>
    );
};

export default FormAppLandingPage;
